import { Component, OnDestroy, OnInit } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { ECharts, EChartsOption } from 'echarts';
import { firstValueFrom, Subscription } from 'rxjs';
import { DashboardService } from '../../../services/dashboard.service';
import { NeighborhoodService } from '../../../services/neighborhood.service';
import { Neighborhood } from '../../../models/neighborhood.model';
import { NgbDateStruct } from '@ng-bootstrap/ng-bootstrap';
import moment from 'moment';
import 'moment/locale/es';
import { IDashboardSummaryResponse } from '../../../models/dashboard-summary-response.model';
import { IDeviceCount, IDeviceModelCount } from '../../../models/dashboard-devices-response.model';

export interface IVisitTypeSummary {
  name: string;
  count: number;
  percentage: string;
  color: string;
}

export interface IHourlyTableRow {
  hour: string;
  counts: number[];
  total: number;
}

/** Conteo de colonias de un estado de la República para el mapa coroplético. */
export interface IStateNeighborhoodSummary {
  /** Nombre del estado tal como viene en el GeoJSON (es el que ECharts empata). */
  name: string;
  total: number;
  active: number;
  /**
   * Proporción de colonias activas sobre el total del estado, de 0 a 100.
   * Es LO QUE MIDE EL COLOR del mapa, y se guarda aquí (y no se recalcula en
   * cada lugar que lo pinta) para que mapa, tooltip y tabla accesible no puedan
   * mostrar números distintos del mismo dato.
   */
  activePercentage: number;
}

/** Estado de cada tarjeta de la sección "Celulares"; se cargan por separado. */
export type DeviceCardState = 'loading' | 'ready' | 'empty' | 'error';

/** Renglón de la lista de sistemas operativos (y de la tabla accesible de marcas). */
export interface IDeviceShareRow {
  name: string;
  total: number;
  percentage: string;
  color: string;
}

/** Renglón del Top 10 de modelos. El renglón "Otros" no lleva marca ni rango. */
export interface IDeviceModelRow {
  rank?: number;
  name: string;
  brand?: string;
  total: number;
  percentage: string;
  /** Ancho relativo de la barra (0-100) contra el modelo con más celulares. */
  barWidth: number;
  isOthers: boolean;
}

/** Nombre de un estado del GeoJSON junto con su forma normalizada. */
interface IStateRegionName {
  original: string;
  normalized: string;
}

@Component({
  selector: 'app-dashboard-page',
  templateUrl: './dashboard-page.component.html',
  styleUrl: './dashboard-page.component.scss'
})
export class DashboardPageComponent implements OnInit, OnDestroy {

  /** Offset fijo (UTC-6) con el que el backend resuelve GET /dashboard. */
  public readonly dashboardTimeZone: number = -6;

  public data?: IDashboardSummaryResponse;
  public visitsTodaySummary?: EChartsOption;
  public model?: NgbDateStruct;
  public date?: { year: number; month: number };

  /** Estado de carga local para pintar skeletons sin reemplazar la página completa. */
  public isLoading: boolean = true;

  /** Fecha seleccionada en formato largo legible, ej. "Viernes 22 de agosto de 2026". */
  public selectedDateLabel: string = '';

  /** Desglose de visitas por tipo con el color de su serie en la gráfica. */
  public visitTypeSummaries: IVisitTypeSummary[] = [];

  /** Hora con mayor número de visitas del día seleccionado. */
  public peakHourLabel?: string;
  public peakHourCount: number = 0;

  /** Descripción textual de la gráfica para lectores de pantalla. */
  public chartAriaLabel: string = '';

  /** Alternativa tabular de la gráfica (visually-hidden) para accesibilidad. */
  public hourlyTableRows: IHourlyTableRow[] = [];

  /** Paleta accesible (contraste >= 4.5:1 sobre blanco) para tipos no mapeados. */
  private readonly seriesColorPalette: string[] = [
    '#01579B',
    '#0E7C66',
    '#B45309',
    '#6D28D9',
    '#B91C1C',
    '#0F766E'
  ];

  /** Color fijo por tipo de visita conocido (clave normalizada sin acentos). */
  private readonly seriesColorByType: Record<string, string> = {
    'a pie': '#01579B',
    'en vehiculo': '#0E7C66',
    'servicio': '#B45309'
  };

  private readonly numberFormatter = new Intl.NumberFormat('es-MX');

  /* -------------------------------------------------------------------------- */
  /*                        ANCHOR STATE MAP (COLONIAS)                         */
  /* -------------------------------------------------------------------------- */

  /** Nombre con el que se registra el GeoJSON de México dentro de ECharts. */
  private readonly stateMapName: string = 'mexico';

  /**
   * GeoJSON de los 32 estados de México.
   * Fuente: https://raw.githubusercontent.com/angelnmara/geojson (mexicoHigh.json).
   *
   * Se sirve como asset y se pide en runtime a propósito: son ~180 KB de
   * coordenadas que no tienen por que viajar en el bundle inicial de la consola,
   * y así el mapa se pinta cuando puede sin retrasar el resto del dashboard.
   */
  private readonly stateMapGeoJsonUrl: string = 'assets/geo/mexico-states.geojson';

  /** Opciones del mapa. Se define solo cuando el GeoJSON ya quedo registrado. */
  public stateMapOptions?: EChartsOption;

  /** Skeleton acotado a la tarjeta del mapa, nunca a la pagina completa. */
  public isStateMapLoading: boolean = true;

  /** Estados con al menos una colonia, ordenados para la tabla accesible. */
  public stateSummaries: IStateNeighborhoodSummary[] = [];

  /** Descripcion textual del mapa para lectores de pantalla. */
  public stateMapAriaLabel: string = '';

  /**
   * Escala rojo -> verde sobre la PROPORCIÓN de colonias activas del estado
   * (0% rojo, 100% verde), pedida así por el usuario. Son tonos de la misma
   * familia que seriesColorPalette (contraste >= 4.5:1 sobre blanco) para no
   * meter una paleta paralela a la consola.
   */
  private readonly stateMapColorScale: string[] = [
    '#B91C1C',
    '#B45309',
    '#0E7C66'
  ];

  /**
   * Gris neutro de Bootstrap 5.3 (gray-300) para los estados SIN colonias.
   * Quedan FUERA de la escala a propósito: en ella el rojo significa "0% de sus
   * colonias activas", y un estado donde no operamos no tiene nada apagado que
   * atender, es ausencia de dato. Meterlo como 0% haria que el mapa mintiera.
   */
  private readonly stateMapEmptyColor: string = '#DEE2E6';

  /**
   * Nombres de estado del GeoJSON, ordenados de MÁS LARGO A MÁS CORTO.
   * El orden es parte del algoritmo de emparejamiento, ver matchRegionName().
   */
  private stateRegionNames: IStateRegionName[] = [];

  /* --------------------------- Encuadre del mapa --------------------------- */

  /**
   * Instancia viva del mapa. Llega por el (chartInit) de la directiva de
   * ngx-echarts, que la emite justo antes del primer setOption
   * (node_modules/ngx-echarts/fesm2022/ngx-echarts.mjs, onOptionsChange).
   */
  private stateMapChart?: ECharts;

  /**
   * Topes de escala del mapa, en múltiplos del encuadre inicial.
   *
   * min 1 porque el encuadre inicial ya es "México completo y centrado": dejar
   * alejar más allá encogería el país dentro de la tarjeta y lo dejaría
   * flotando, sin ganar ni un dato.
   * max 6 porque a esa escala los estados más chicos (Tlaxcala, Morelos, Ciudad
   * de México), que en el encuadre inicial miden ~1.5% del ancho del mapa, pasan
   * a ~9% del viewport, tamaño de sobra para señalarlos y leer su etiqueta. Más
   * allá de 6 se ve menos de un sexto del país a la vez y, como la navegación es
   * SOLO por arrastre (sin rueda), volver a ubicarse se vuelve tedioso.
   *
   * ECharts respeta estos topes en los dos caminos: al despachar geoRoam, en
   * echarts/lib/action/roamHelper.js updateCenterAndZoom() recorta con
   *     zoom = Math.max(Math.min(previousZoom * zoom, zoomMax), zoomMin) / previousZoom;
   * y al reconstruir la vista, en echarts/lib/coord/View.js setZoom().
   */
  private readonly stateMapScaleLimit: { min: number; max: number } = {
    min: 1,
    max: 6
  };

  /**
   * Factor de acercamiento por clic. 1.4 ≈ raíz de 2, es decir dos clics
   * duplican la escala: un paso perceptible pero que no desubica. Con este paso
   * se llega del encuadre inicial al tope en 6 clics.
   */
  private readonly stateMapZoomStep: number = 1.4;

  /** Escala vigente del mapa. Arranca en el encuadre inicial. */
  private stateMapZoom: number = 1;

  /**
   * Centro vigente del mapa en coordenadas del GeoJSON, o undefined mientras
   * nadie lo haya movido (ahí manda el centro por defecto que calcula ECharts).
   */
  private stateMapCenter?: number[];

  /** Última emisión de colonias recibida del NeighborhoodService. */
  private loadedNeighborhoods: Neighborhood[] = [];

  /** Indica que subNeighborhoods ya emitio al menos una vez. */
  private hasNeighborhoodsEmission: boolean = false;

  private subscriptionNeighborhoods?: Subscription;

  /* -------------------------------------------------------------------------- */
  /*                            ANCHOR DEVICES (CELULARES)                      */
  /* -------------------------------------------------------------------------- */

  /**
   * Una sola bandera para TODA la sección: false = solo activos (default del
   * API), true = activos + inactivos. Se manda como includeInactive a los cuatro
   * endpoints.
   */
  public includeInactiveDevices: boolean = false;

  /** Cuántos modelos se listan antes de agrupar el resto en "Otros". */
  private readonly deviceModelsTopLimit: number = 10;

  public devicesOsState: DeviceCardState = 'loading';
  public devicesOsTotal: number = 0;
  public devicesOsRows: IDeviceShareRow[] = [];
  public devicesOsOptions?: EChartsOption;
  public devicesOsAriaLabel: string = '';

  public devicesBrandState: DeviceCardState = 'loading';
  public devicesBrandRows: IDeviceShareRow[] = [];
  public devicesBrandOptions?: EChartsOption;
  public devicesBrandAriaLabel: string = '';
  /** Alto de la gráfica de marcas: crece con el número de barras. */
  public devicesBrandChartHeight: number = 240;

  public devicesModelState: DeviceCardState = 'loading';
  public devicesModelRows: IDeviceModelRow[] = [];
  public devicesModelTotal: number = 0;

  public devicesFoldableState: DeviceCardState = 'loading';
  public devicesFoldableRows: IDeviceModelRow[] = [];
  /** totalFoldables del API: la cifra destacada de la tarjeta. */
  public devicesFoldableTotal: number = 0;
  /** totalDevices del API: base del % y llave para elegir el texto del vacío. */
  public devicesFoldableDevicesTotal: number = 0;
  /** % de plegables sobre totalDevices, ya formateado a un decimal. */
  public devicesFoldablePercentage: string = '0.0';

  /**
   * Contadores de petición por tarjeta. Si el usuario alterna "Mostrar no
   * activos" (o pulsa "Reintentar") mientras otra petición sigue en vuelo, la
   * respuesta vieja se descarta en lugar de pisar a la nueva.
   */
  private devicesOsRequestId: number = 0;
  private devicesBrandRequestId: number = 0;
  private devicesModelRequestId: number = 0;
  private devicesFoldableRequestId: number = 0;

  constructor(
    public dashboardService: DashboardService,
    private neighborhoodService: NeighborhoodService,
    private http: HttpClient
  ) {}

  ngOnInit() {
    const dashboardDate = moment.utc().add(this.dashboardTimeZone, 'hours');
    this.model = {
      year: dashboardDate.year(),
      month: dashboardDate.month() + 1,
      day: dashboardDate.date(),
    };
    this.getData();

    // NeighborhoodService.getAll() ya se dispara desde el constructor del propio
    // servicio (providedIn: 'root'), y subNeighborhoods es un ReplaySubject(1)
    // que reentrega la última lista al suscriptor tardio. Por eso aquí SOLO nos
    // suscribimos y NO se llama getAll(): la consola ya sufre un fan-out de
    // peticiones al arrancar y console-api tiene arranques en frio que mueren en
    // 504, así que este mapa se monta con CERO peticiones extra a la API.
    this.subscriptionNeighborhoods = this.neighborhoodService.subNeighborhoods.subscribe(
      (neighborhoods) => {
        this.loadedNeighborhoods = neighborhoods ?? [];
        this.hasNeighborhoodsEmission = true;
        this.buildStateMap();
      }
    );

    this.loadStateMap();

    // Las cuatro tarjetas de celulares cargan por separado y sin await: ninguna
    // bloquea a las otras ni al resto del dashboard (render progresivo).
    this.loadDevices();
  }

  ngOnDestroy(): void {
    this.subscriptionNeighborhoods?.unsubscribe();
  }

  get hasVisits(): boolean {
    return (this.data?.totalVisits ?? 0) > 0;
  }

  async getData() {

    this.isLoading = true;
    this.setSelectedDateLabel();

    const date = this.model
      ? moment({
          year: this.model.year,
          month: this.model.month - 1,
          day: this.model.day
        }).format('YYYY-MM-DD')
      : moment().format('YYYY-MM-DD');

    this.data = await this.dashboardService.getAll(date);

    if (!this.data) {
      this.resetSummaries();
      this.isLoading = false;
      return;
    }

    // Extraer xAxis
    const xAxis = this.data.visitsByHour.map(v => v.hour);

    // Recolectar todos los tipos únicos
    const typeSet = new Set<string>();
    for (const hourData of this.data.visitsByHour) {
      for (const type of hourData.types) {
        typeSet.add(type.name);
      }
    }
    const types = Array.from(typeSet).sort();

    const colors = types.map((typeName, index) => this.getSeriesColor(typeName, index));

    this.setVisitTypeSummaries(types, colors);
    this.setPeakHour();
    this.setHourlyTableRows(types);
    this.setChartAriaLabel();

    // Construir series para ECharts
    const series = types.map((typeName, index) => ({
      name: typeName,
      type: 'bar',
      stack: 'total',
      barMaxWidth: 22,
      itemStyle: index === types.length - 1
        ? { borderRadius: [3, 3, 0, 0] }
        : {},
      label: {
        show: true,
        position: 'inside',
        fontSize: 10,
        fontWeight: 600,
        color: '#FFFFFF',
        // Oculta los data labels de las barras vacías para eliminar ruido visual.
        formatter: (params: any) => params.value > 0
          ? this.numberFormatter.format(params.value)
          : ''
      },
      labelLayout: { hideOverlap: true },
      emphasis: { focus: 'series' },
      data: this.data!.visitsByHour.map(hourData => {
        const found = hourData.types.find(t => t.name === typeName);
        return found ? found.count : 0;
      })
    }));

    this.visitsTodaySummary = {
      color: colors,
      aria: { enabled: true },
      animationDuration: 300,
      textStyle: {
        fontFamily: 'inherit',
        color: '#334155'
      },
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'shadow' },
        borderColor: '#E2E8F0',
        borderWidth: 1,
        backgroundColor: '#FFFFFF',
        extraCssText: 'box-shadow: 0 8px 24px rgba(15, 23, 42, 0.12); border-radius: 8px;',
        textStyle: {
          color: '#0F172A',
          fontSize: 12
        },
        formatter: (params: any) => this.buildTooltip(params),
      },
      legend: {
        bottom: 0,
        icon: 'circle',
        itemWidth: 10,
        itemHeight: 10,
        itemGap: 20,
        textStyle: {
          fontSize: 12,
          color: '#334155'
        }
      },
      grid: {
        top: 16,
        left: 8,
        right: 8,
        bottom: 34,
        containLabel: true
      },
      xAxis: {
        type: 'category',
        data: xAxis,
        axisTick: { show: false },
        axisLine: { lineStyle: { color: '#CBD5E1' } },
        axisLabel: {
          color: '#475569',
          fontSize: 11,
          hideOverlap: true
        }
      },
      yAxis: {
        type: 'value',
        minInterval: 1,
        axisLine: { show: false },
        axisTick: { show: false },
        axisLabel: {
          color: '#475569',
          fontSize: 11
        },
        splitLine: {
          lineStyle: {
            color: '#E2E8F0',
            type: 'dashed'
          }
        }
      },
      series: series as any
    };

    this.isLoading = false;
  }

  async moveDay(
    increment: number
  ) {

    const movedDate = this.model
      ? moment({
          year: this.model.year,
          month: this.model.month - 1,
          day: this.model.day
        }).add(increment, 'days')
      : moment().add(increment, 'days');

    this.model = {
      year: movedDate.year(),
      month: movedDate.month() + 1,
      day: movedDate.date()
    };

    await this.getData();
  }

  /* -------------------------------------------------------------------------- */
  /*                        ANCHOR STATE MAP ZOOM CONTROLS                      */
  /* -------------------------------------------------------------------------- */

  /** Guarda la instancia del mapa que entrega ngx-echarts en (chartInit). */
  onStateMapInit(
    chart: ECharts
  ) {
    this.stateMapChart = chart;
  }

  /**
   * Acerca (direction positivo) o aleja (direction negativo) el mapa un paso.
   *
   * En los topes NINGÚN botón se deshabilita: updateCenterAndZoom() de
   * echarts/lib/action/roamHelper.js recorta el factor contra scaleLimit antes
   * de aplicarlo, así que al llegar al límite el factor efectivo queda en 1 y el
   * clic simplemente no mueve nada. Aquí los deshabilitados se reservan para
   * operaciones asíncronas en curso, no para estados de tope.
   */
  zoomStateMap(
    direction: number
  ) {

    const chart = this.stateMapChart;

    if (!chart || chart.isDisposed()) return;

    const origin = this.getStateMapZoomOrigin(chart);

    if (!origin) return;

    chart.dispatchAction({
      type: 'geoRoam',
      componentType: 'series',
      // seriesIndex y NO seriesId, aunque las dos formas identifiquen la serie.
      // En echarts/lib/chart/map/MapView.js, render() se SALTA el redibujado
      // cuando el payload trae `componentType === 'series' && payload.seriesId
      // === mapModel.id`, porque asume que el gesto vino del RoamController, que
      // ya movió el grupo a mano. Despachando desde un botón no hay tal gesto:
      // con seriesId el modelo cambiaría y la pantalla no. Con seriesIndex la
      // condición no se cumple, MapDraw.draw() corre y aplica la transformación.
      seriesIndex: 0,
      // `zoom` del payload es un FACTOR RELATIVO sobre la escala actual, no un
      // nivel absoluto: updateCenterAndZoom() hace previousZoom * zoom.
      zoom: direction > 0
        ? this.stateMapZoomStep
        : 1 / this.stateMapZoomStep,
      originX: origin[0],
      originY: origin[1]
    });

    // A propósito NO se copia aquí el encuadre a la clase. La fuente de verdad
    // del zoom y del centro es el propio gráfico, y arrastrar con el mouse
    // tampoco pasa por este método: mantener una copia en paralelo solo abriría
    // la puerta a que las dos versiones se separen. Se lee del gráfico en el
    // único momento en que hace falta, justo antes de rehacer las opciones.
  }

  /**
   * Devuelve el mapa a su vista inicial COMPLETA: escala Y desplazamiento.
   *
   * No se resuelve con un geoRoam inverso porque ese camino solo desharía la
   * escala y dejaría el mapa corrido. Se reconstruyen las opciones con el centro
   * y el zoom iniciales y se deja que ECharts rehaga el sistema de coordenadas:
   * resizeGeo() (echarts/lib/coord/geo/geoCreator.js) llama setCenter(null) —que
   * en View.js retorna sin tocar nada y deja mandando getDefaultCenter()— y
   * setZoom(1). Es el MISMO camino que corre en el primer pintado, así que el
   * encuadre resultante es idéntico al inicial por construcción, sin aritmética
   * de por medio que pueda acumular error.
   */
  resetStateMapView() {

    if (!this.stateMapOptions) return;

    this.stateMapZoom = this.stateMapScaleLimit.min;
    this.stateMapCenter = undefined;

    // preserveView en false: se reconstruye SIN copiar antes el encuadre
    // vigente, que es precisamente lo que hay que descartar.
    this.buildStateMap(false);
  }

  /* -------------------------------------------------------------------------- */
  /*                          ANCHOR DEVICES (CELULARES)                        */
  /* -------------------------------------------------------------------------- */

  /** "activos" solo aplica cuando se filtran los inactivos. */
  get devicesScopeLabel(): string {
    return this.includeInactiveDevices ? 'registrados' : 'activos';
  }

  get devicesEmptyTitle(): string {
    return `Sin celulares ${this.devicesScopeLabel}`;
  }

  get devicesEmptyText(): string {
    return this.includeInactiveDevices
      ? 'Aún no hay instalaciones de la app.'
      : 'Aún no hay instalaciones activas de la app.';
  }

  /**
   * Si no hay ningún celular, el vacío de Plegables dice lo mismo que las otras
   * tarjetas; si hay celulares pero ninguno es plegable, lo dice explícitamente.
   */
  get devicesFoldableEmptyTitle(): string {
    return this.devicesFoldableDevicesTotal
      ? 'Sin celulares plegables'
      : this.devicesEmptyTitle;
  }

  get devicesFoldableEmptyText(): string {
    return this.devicesFoldableDevicesTotal
      ? `Ninguno de los celulares ${this.devicesScopeLabel} es plegable.`
      : this.devicesEmptyText;
  }

  /** Alterna activos / activos + inactivos y recarga las cuatro tarjetas. */
  toggleInactiveDevices() {
    this.includeInactiveDevices = !this.includeInactiveDevices;
    this.loadDevices();
  }

  /** Dispara las cuatro tarjetas en paralelo, cada una con su propio estado. */
  loadDevices() {
    this.loadDevicesByOs();
    this.loadDevicesByBrand();
    this.loadDevicesByModel();
    this.loadDevicesFoldables();
  }

  async loadDevicesByOs() {

    const requestId = ++this.devicesOsRequestId;
    this.devicesOsState = 'loading';

    try {

      const response = await this.dashboardService.getDevicesByOs(this.includeInactiveDevices);

      if (requestId !== this.devicesOsRequestId) return;

      const items = this.sortDeviceCounts(response.os ?? []);
      const total = this.sumDeviceCounts(items);

      if (!total) {
        this.resetDevicesOs();
        this.devicesOsState = 'empty';
        return;
      }

      this.devicesOsTotal = response.totalDevices || total;
      this.devicesOsRows = this.buildDeviceShareRows(items, total);
      this.devicesOsAriaLabel = `Gráfica de dona con los celulares ${this.devicesScopeLabel} por sistema operativo. `
        + `Total ${this.devicesOsTotal}. `
        + this.devicesOsRows.map(row => `${row.name}: ${row.total}, ${row.percentage} por ciento`).join('; ')
        + '.';

      this.devicesOsOptions = {
        color: this.devicesOsRows.map(row => row.color),
        aria: { enabled: true },
        animationDuration: 300,
        textStyle: {
          fontFamily: 'inherit',
          color: '#334155'
        },
        tooltip: {
          trigger: 'item',
          borderColor: '#E2E8F0',
          borderWidth: 1,
          backgroundColor: '#FFFFFF',
          extraCssText: 'box-shadow: 0 8px 24px rgba(15, 23, 42, 0.12); border-radius: 8px;',
          textStyle: {
            color: '#0F172A',
            fontSize: 12
          },
          formatter: (params: any) => this.buildDeviceTooltip(
            params?.name,
            Number(params?.value) || 0,
            total,
            params?.color
          )
        },
        // El total va al centro de la dona; el desglose vive en la lista de al
        // lado, por eso no hay legend de ECharts (sería información duplicada).
        title: {
          text: this.numberFormatter.format(this.devicesOsTotal),
          subtext: 'celulares',
          left: 'center',
          top: '38%',
          textStyle: {
            color: '#0F172A',
            fontSize: 22,
            fontWeight: 700
          },
          subtextStyle: {
            color: '#475569',
            fontSize: 12
          }
        },
        series: [
          {
            type: 'pie',
            radius: ['58%', '82%'],
            center: ['50%', '50%'],
            avoidLabelOverlap: true,
            label: { show: false },
            labelLine: { show: false },
            itemStyle: {
              borderColor: '#FFFFFF',
              borderWidth: 2
            },
            emphasis: { scale: true, scaleSize: 4 },
            data: this.devicesOsRows.map(row => ({ name: row.name, value: row.total }))
          }
        ] as any
      };

      this.devicesOsState = 'ready';

    } catch {

      if (requestId !== this.devicesOsRequestId) return;

      this.resetDevicesOs();
      this.devicesOsState = 'error';
    }
  }

  async loadDevicesByBrand() {

    const requestId = ++this.devicesBrandRequestId;
    this.devicesBrandState = 'loading';

    try {

      const response = await this.dashboardService.getDevicesByBrand(this.includeInactiveDevices);

      if (requestId !== this.devicesBrandRequestId) return;

      const items = this.sortDeviceCounts(response.brands ?? []);
      const total = this.sumDeviceCounts(items);

      if (!total) {
        this.resetDevicesBrand();
        this.devicesBrandState = 'empty';
        return;
      }

      const brandColor = this.seriesColorPalette[0];

      this.devicesBrandRows = this.buildDeviceShareRows(items, total)
        .map(row => ({ ...row, color: brandColor }));
      this.devicesBrandAriaLabel = `Gráfica de barras horizontales con los celulares ${this.devicesScopeLabel} por marca. `
        + this.devicesBrandRows.map(row => `${row.name}: ${row.total}`).join('; ')
        + '.';

      // 32px por barra + margen de ejes; nunca menos que la dona de al lado.
      this.devicesBrandChartHeight = Math.max(240, this.devicesBrandRows.length * 32 + 40);

      // En un eje vertical de categorías ECharts pone la primera categoría ABAJO.
      // Se invierte (yAxis.inverse) para que la marca con más celulares quede
      // arriba, como se lee una lista.
      const brandNames = this.devicesBrandRows.map(row => row.name);
      const brandTotals = this.devicesBrandRows.map(row => row.total);

      this.devicesBrandOptions = {
        color: [brandColor],
        aria: { enabled: true },
        animationDuration: 300,
        textStyle: {
          fontFamily: 'inherit',
          color: '#334155'
        },
        tooltip: {
          trigger: 'axis',
          axisPointer: { type: 'shadow' },
          borderColor: '#E2E8F0',
          borderWidth: 1,
          backgroundColor: '#FFFFFF',
          extraCssText: 'box-shadow: 0 8px 24px rgba(15, 23, 42, 0.12); border-radius: 8px;',
          textStyle: {
            color: '#0F172A',
            fontSize: 12
          },
          formatter: (params: any) => {
            const item = Array.isArray(params) ? params[0] : params;
            return this.buildDeviceTooltip(
              item?.name,
              Number(item?.value) || 0,
              total,
              brandColor
            );
          }
        },
        grid: {
          top: 8,
          left: 8,
          right: 48,
          bottom: 8,
          containLabel: true
        },
        xAxis: {
          type: 'value',
          minInterval: 1,
          axisLine: { show: false },
          axisTick: { show: false },
          axisLabel: {
            color: '#475569',
            fontSize: 11
          },
          splitLine: {
            lineStyle: {
              color: '#E2E8F0',
              type: 'dashed'
            }
          }
        },
        yAxis: {
          type: 'category',
          inverse: true,
          data: brandNames,
          axisTick: { show: false },
          axisLine: { lineStyle: { color: '#CBD5E1' } },
          axisLabel: {
            color: '#334155',
            fontSize: 12,
            width: 120,
            overflow: 'truncate'
          }
        },
        series: [
          {
            type: 'bar',
            barMaxWidth: 18,
            itemStyle: { borderRadius: [0, 3, 3, 0] },
            label: {
              show: true,
              position: 'right',
              fontSize: 11,
              fontWeight: 600,
              color: '#0F172A',
              formatter: (params: any) => this.numberFormatter.format(Number(params.value) || 0)
            },
            data: brandTotals
          }
        ] as any
      };

      this.devicesBrandState = 'ready';

    } catch {

      if (requestId !== this.devicesBrandRequestId) return;

      this.resetDevicesBrand();
      this.devicesBrandState = 'error';
    }
  }

  async loadDevicesByModel() {

    const requestId = ++this.devicesModelRequestId;
    this.devicesModelState = 'loading';

    try {

      const response = await this.dashboardService.getDevicesByModel(this.includeInactiveDevices);

      if (requestId !== this.devicesModelRequestId) return;

      const items = this.sortDeviceCounts(response.models ?? []) as IDeviceModelCount[];
      const total = this.sumDeviceCounts(items);

      if (!total) {
        this.devicesModelRows = [];
        this.devicesModelTotal = 0;
        this.devicesModelState = 'empty';
        return;
      }

      this.devicesModelRows = this.buildDeviceModelRows(items, total);
      this.devicesModelTotal = response.totalDevices || total;
      this.devicesModelState = 'ready';

    } catch {

      if (requestId !== this.devicesModelRequestId) return;

      this.devicesModelRows = [];
      this.devicesModelTotal = 0;
      this.devicesModelState = 'error';
    }
  }

  async loadDevicesFoldables() {

    const requestId = ++this.devicesFoldableRequestId;
    this.devicesFoldableState = 'loading';

    try {

      const response = await this.dashboardService.getDevicesFoldables(this.includeInactiveDevices);

      if (requestId !== this.devicesFoldableRequestId) return;

      const items = this.sortDeviceCounts(response.foldables ?? []);
      const total = this.sumDeviceCounts(items);
      const devicesTotal = Number(response.totalDevices) || 0;

      if (!total) {
        this.resetDevicesFoldable();
        // Se conserva para que el vacío distinga "sin celulares" de "sin plegables".
        this.devicesFoldableDevicesTotal = devicesTotal;
        this.devicesFoldableState = 'empty';
        return;
      }

      const foldablesTotal = Number(response.totalFoldables) || total;

      // Top 10 + "Otros" igual que la tabla de modelos; el % de cada renglón es
      // sobre el total de plegables (el % sobre todos los celulares va arriba).
      this.devicesFoldableRows = this.buildDeviceModelRows(items, total);
      this.devicesFoldableTotal = foldablesTotal;
      this.devicesFoldableDevicesTotal = devicesTotal;
      this.devicesFoldablePercentage = this.formatDevicePercentage(foldablesTotal, devicesTotal);
      this.devicesFoldableState = 'ready';

    } catch {

      if (requestId !== this.devicesFoldableRequestId) return;

      this.resetDevicesFoldable();
      this.devicesFoldableState = 'error';
    }
  }

  /* -------------------------------------------------------------------------- */
  /*                             ANCHOR PRIVATE HELPERS                         */
  /* -------------------------------------------------------------------------- */

  /** Genera el HTML del tooltip con la etiqueta y el valor bien separados. */
  private buildTooltip(
    params: any
  ): string {

    const paramArray = Array.isArray(params) ? params : [params];
    const hour = paramArray[0]?.axisValue ?? '';
    const total = paramArray.reduce(
      (sum: number, item: any) => sum + (Number(item.value) || 0),
      0
    );

    let tooltipHtml = `<div style="font-weight:600;color:#0F172A;margin-bottom:6px;">${hour}</div>`;

    if (!total) {
      tooltipHtml += `<div style="color:#475569;">Sin visitas en esta hora</div>`;
      return tooltipHtml;
    }

    tooltipHtml += `
      <div style="display:flex;align-items:center;gap:16px;color:#475569;margin-bottom:6px;">
        <span>Total de visitas</span>
        <span style="margin-left:auto;font-weight:600;color:#0F172A;">${this.numberFormatter.format(total)}</span>
      </div>
    `;

    for (const item of paramArray) {

      const value = Number(item.value) || 0;

      // Solo se listan los tipos con visitas para no saturar el tooltip.
      if (!value) continue;

      const percent = ((value / total) * 100).toFixed(1);

      tooltipHtml += `
        <div style="display:flex;align-items:center;gap:16px;min-width:200px;margin-top:2px;">
          <span style="display:flex;align-items:center;gap:8px;color:#334155;">
            <span style="display:inline-block;width:10px;height:10px;border-radius:50%;background-color:${item.color};"></span>
            <span>${item.seriesName}</span>
          </span>
          <span style="margin-left:auto;font-weight:600;color:#0F172A;white-space:nowrap;">${this.numberFormatter.format(value)} &middot; ${percent}%</span>
        </div>
      `;
    }

    return tooltipHtml;
  }

  /* ----------------------------- Devices helpers ---------------------------- */

  /** Orden descendente por total y, en empate, alfabético. No muta el arreglo. */
  private sortDeviceCounts<T extends IDeviceCount>(
    items: T[]
  ): T[] {
    return [...items]
      .filter(item => (Number(item.total) || 0) > 0)
      .sort(
        (first, second) =>
          second.total - first.total
          || String(first.name ?? '').localeCompare(String(second.name ?? ''), 'es')
      );
  }

  private sumDeviceCounts(
    items: IDeviceCount[]
  ): number {
    return items.reduce((sum, item) => sum + (Number(item.total) || 0), 0);
  }

  private formatDevicePercentage(
    value: number,
    total: number
  ): string {
    return total ? ((value / total) * 100).toFixed(1) : '0.0';
  }

  private buildDeviceShareRows(
    items: IDeviceCount[],
    total: number
  ): IDeviceShareRow[] {
    return items.map((item, index) => ({
      name: item.name,
      total: item.total,
      percentage: this.formatDevicePercentage(item.total, total),
      color: this.seriesColorPalette[index % this.seriesColorPalette.length]
    }));
  }

  private resetDevicesOs() {
    this.devicesOsTotal = 0;
    this.devicesOsRows = [];
    this.devicesOsOptions = undefined;
    this.devicesOsAriaLabel = '';
  }

  private resetDevicesBrand() {
    this.devicesBrandRows = [];
    this.devicesBrandOptions = undefined;
    this.devicesBrandAriaLabel = '';
    this.devicesBrandChartHeight = 240;
  }

  private resetDevicesFoldable() {
    this.devicesFoldableRows = [];
    this.devicesFoldableTotal = 0;
    this.devicesFoldableDevicesTotal = 0;
    this.devicesFoldablePercentage = '0.0';
  }

  /**
   * Primeros `deviceModelsTopLimit` modelos (ya ordenados) y, si sobran, un
   * renglón "Otros" con la suma del resto. El % de cada renglón es sobre `total`.
   * Lo comparten "Top 10 modelos" y "Plegables".
   */
  private buildDeviceModelRows(
    items: IDeviceModelCount[],
    total: number
  ): IDeviceModelRow[] {

    const topModels = items.slice(0, this.deviceModelsTopLimit);
    const otherModels = items.slice(this.deviceModelsTopLimit);
    const maxTotal = topModels[0]?.total || 1;

    const rows: IDeviceModelRow[] = topModels.map((model, index) => ({
      rank: index + 1,
      name: model.name,
      brand: model.brand,
      total: model.total,
      percentage: this.formatDevicePercentage(model.total, total),
      barWidth: (model.total / maxTotal) * 100,
      isOthers: false
    }));

    if (otherModels.length) {

      const othersTotal = this.sumDeviceCounts(otherModels);

      rows.push({
        name: `Otros (${otherModels.length} ${otherModels.length === 1 ? 'modelo' : 'modelos'})`,
        total: othersTotal,
        percentage: this.formatDevicePercentage(othersTotal, total),
        barWidth: Math.min(100, (othersTotal / maxTotal) * 100),
        isOthers: true
      });
    }

    return rows;
  }

  /**
   * Tooltip de las gráficas de celulares. El nombre viene tal cual del API
   * (marca, sistema operativo), así que se escapa antes de meterlo al HTML que
   * ECharts inyecta con innerHTML.
   */
  private buildDeviceTooltip(
    name: string | undefined,
    value: number,
    total: number,
    color: string | undefined
  ): string {

    const percent = this.formatDevicePercentage(value, total);
    const label = value === 1 ? 'celular' : 'celulares';

    return `
      <div style="display:flex;align-items:center;gap:8px;font-weight:600;color:#0F172A;margin-bottom:6px;">
        <span style="display:inline-block;width:10px;height:10px;border-radius:50%;background-color:${this.escapeHtml(color ?? '')};"></span>
        <span>${this.escapeHtml(name ?? '')}</span>
      </div>
      <div style="display:flex;align-items:center;gap:16px;min-width:180px;color:#475569;">
        <span>${this.numberFormatter.format(value)} ${label}</span>
        <span style="margin-left:auto;font-weight:600;color:#0F172A;white-space:nowrap;">${percent}%</span>
      </div>
    `;
  }

  private escapeHtml(
    value: string
  ): string {
    return String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  private setSelectedDateLabel() {

    if (!this.model) {
      this.selectedDateLabel = '';
      return;
    }

    const selectedDay = moment({
      year: this.model.year,
      month: this.model.month - 1,
      day: this.model.day
    }).locale('es');

    const label = selectedDay.format('dddd D [de] MMMM [de] YYYY');

    this.selectedDateLabel = label.charAt(0).toUpperCase() + label.slice(1);
  }

  private setVisitTypeSummaries(
    types: string[],
    colors: string[]
  ) {

    const total = this.data?.totalVisits ?? 0;

    this.visitTypeSummaries = types.map((typeName, index) => {

      const count = (this.data?.visitsByHour ?? []).reduce(
        (sum, hourData) => sum + (hourData.types.find(t => t.name === typeName)?.count ?? 0),
        0
      );

      return {
        name: typeName,
        count,
        percentage: total ? ((count / total) * 100).toFixed(1) : '0.0',
        color: colors[index]
      };
    });
  }

  private setPeakHour() {

    const hours = this.data?.visitsByHour ?? [];

    const peakHour = hours.reduce<{ hour: string; total: number } | undefined>(
      (max, hourData) => !max || hourData.total > max.total ? hourData : max,
      undefined
    );

    if (!peakHour || !peakHour.total) {
      this.peakHourLabel = undefined;
      this.peakHourCount = 0;
      return;
    }

    this.peakHourLabel = peakHour.hour;
    this.peakHourCount = peakHour.total;
  }

  private setHourlyTableRows(
    types: string[]
  ) {

    this.hourlyTableRows = (this.data?.visitsByHour ?? []).map(hourData => ({
      hour: hourData.hour,
      counts: types.map(
        typeName => hourData.types.find(t => t.name === typeName)?.count ?? 0
      ),
      total: hourData.total
    }));
  }

  private setChartAriaLabel() {

    const total = this.data?.totalVisits ?? 0;

    const breakdown = this.visitTypeSummaries
      .map(summary => `${summary.name}: ${summary.count}`)
      .join(', ');

    this.chartAriaLabel = `Gráfica de barras apiladas con las visitas por hora del ${this.selectedDateLabel}. `
      + `Total ${total} visitas. ${breakdown}.`;
  }

  private resetSummaries() {
    this.visitTypeSummaries = [];
    this.hourlyTableRows = [];
    this.peakHourLabel = undefined;
    this.peakHourCount = 0;
    this.chartAriaLabel = '';
    this.visitsTodaySummary = undefined;
  }

  private getSeriesColor(
    typeName: string,
    index: number
  ): string {

    const normalized = typeName
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .trim();

    return this.seriesColorByType[normalized]
      ?? this.seriesColorPalette[index % this.seriesColorPalette.length];
  }

  /* -------------------------------------------------------------------------- */
  /*                          ANCHOR STATE MAP HELPERS                          */
  /* -------------------------------------------------------------------------- */

  /** Descarga el GeoJSON, lo registra en ECharts e intenta construir el mapa. */
  private async loadStateMap(): Promise<void> {

    try {

      // Se importa echarts con el MISMO especificador dinámico que usa
      // NgxEchartsModule.forRoot en app.module.ts. Webpack agrupa ambos en el
      // mismo chunk perezoso, así que registrar el mapa aquí lo deja visible
      // para la directiva sin descargar la librería dos veces ni arrastrarla al
      // bundle inicial.
      const [geoJson, echartsModule] = await Promise.all([
        firstValueFrom(this.http.get<any>(this.stateMapGeoJsonUrl)),
        import('echarts')
      ]);

      echartsModule.registerMap(this.stateMapName, geoJson);

      this.stateRegionNames = (geoJson?.features ?? [])
        .map((feature: any) => String(feature?.properties?.name ?? '').trim())
        .filter((regionName: string) => regionName.length > 0)
        .map((regionName: string) => ({
          original: regionName,
          normalized: this.normalizeStateName(regionName)
        }))
        // Orden descendente por longitud. NO es cosmético: es lo que hace
        // correcto el emparejamiento por prefijo de matchRegionName().
        .sort(
          (first: IStateRegionName, second: IStateRegionName) =>
            second.normalized.length - first.normalized.length
        );

      this.buildStateMap();

    } catch {

      // Falla en silencio: la tarjeta del mapa simplemente no se pinta y el
      // resto del dashboard sigue funcionando igual.
      this.stateMapOptions = undefined;
      this.isStateMapLoading = false;
    }
  }

  /**
   * Arma las opciones del mapa. Es idempotente y se llama tanto desde la carga
   * del GeoJSON como desde la emisión de colonias, porque no hay garantía de
   * orden entre las dos: gana la que llegue al final.
   *
   * `preserveView` en true (el caso normal) conserva el encuadre que el usuario
   * tenga puesto; en false lo devuelve al inicial, que es lo que hace el botón
   * de restablecer.
   */
  private buildStateMap(
    preserveView: boolean = true
  ) {

    // Se espera a tener AMBAS piezas antes de pintar. Si se pintara solo con el
    // GeoJSON, el mapa apareceria todo gris y se recolorearia despues, que se
    // lee como un parpadeo de datos incorrectos.
    if (!this.stateRegionNames.length || !this.hasNeighborhoodsEmission) return;

    // OJO: cada emisión de subNeighborhoods rehace este objeto, y un objeto
    // nuevo hace que la directiva de ngx-echarts llame setOption(options, TRUE),
    // es decir notMerge (ver onOptionsChange en
    // node_modules/ngx-echarts/fesm2022/ngx-echarts.mjs). Con notMerge, ECharts
    // reconstruye el sistema de coordenadas y resizeGeo() vuelve a aplicar
    // `geoModel.get('center')` y `geoModel.get('zoom')` DE LA OPCIÓN
    // (echarts/lib/coord/geo/geoCreator.js). Si no se copiara aquí el encuadre
    // vigente, actualizar colonias tiraría en silencio el zoom del usuario.
    if (preserveView) this.captureStateMapView();

    this.setStateSummaries();
    this.setStateMapAriaLabel();

    // Cada dato lleva el resumen completo: ECharts colorea con `value` (el
    // porcentaje) y el tooltip lee del mismo objeto el conteo crudo, que es lo
    // que evita que el número del tooltip y el color puedan desincronizarse.
    const mapData = this.stateSummaries.map(summary => ({
      ...summary,
      value: summary.activePercentage
    }));

    this.stateMapOptions = {
      aria: { enabled: true },
      animationDuration: 300,
      textStyle: {
        fontFamily: 'inherit',
        color: '#334155'
      },
      tooltip: {
        trigger: 'item',
        borderColor: '#E2E8F0',
        borderWidth: 1,
        backgroundColor: '#FFFFFF',
        extraCssText: 'box-shadow: 0 8px 24px rgba(15, 23, 42, 0.12); border-radius: 8px;',
        textStyle: {
          color: '#0F172A',
          fontSize: 12
        },
        formatter: (params: any) => this.buildStateTooltip(params)
      },
      visualMap: {
        type: 'continuous',
        // Dominio FIJO: el color mide un porcentaje, y un porcentaje siempre va
        // de 0 a 100. Así el mismo tono significa lo mismo entre recargas y
        // entre residenciales, cosa que una escala relativa al dato no cumple.
        min: 0,
        max: 100,
        precision: 0,
        orient: 'horizontal',
        left: 'center',
        bottom: 0,
        itemWidth: 12,
        // Con orient 'horizontal' ECharts invierte el par (ver itemSize en
        // visualMap/VisualMapModel.js), asi que itemHeight es el LARGO de la barra
        // y itemWidth su grosor. La barra crece de 130 a 160 en la misma
        // proporcion en que crecio el mapa (~+30% de lado en >=1200px): con un
        // pais de hasta 897px de ancho encima, una barra de 130 se leia como una
        // marca suelta. El grosor NO se toca: es el que fija el alto real de la
        // leyenda (22px medidos), del que depende la holgura contra el mapa.
        itemHeight: 160,
        calculable: false,
        // Los extremos se nombran para que la leyenda diga QUÉ mide el color,
        // no solo cuánto. El porcentaje y el conteo exacto de cada estado viven
        // ademas en el tooltip y en la tabla accesible: el color rojo-verde
        // nunca es el unico portador del dato (WCAG 1.4.1), que es lo que lo
        // vuelve seguro ante daltonismo rojo-verde.
        text: ['100% activas', '0% activas'],
        textStyle: {
          color: '#475569',
          fontSize: 11
        },
        inRange: { color: this.stateMapColorScale }
      },
      series: [
        {
          type: 'map',
          map: this.stateMapName,
          // 'move' y NO true: en echarts/lib/component/helper/RoamController.js,
          // enable() suscribe 'mousedown'/'mousemove'/'mouseup' cuando el valor
          // es true, 'move' o 'pan', y suscribe 'mousewheel'/'pinch' SOLO cuando
          // es true, 'scale' o 'zoom'. Su propio comentario lo dice: "only enable
          // needed types. For example, if 'zoom' is not needed, 'zoom' should not
          // be enabled, otherwise default mousewheel behaviour (scroll page) will
          // be disabled". Con 'move' el mapa se arrastra pero la rueda del mouse
          // sigue haciendo scroll de la página, que es justo lo que se quiere:
          // con roam true, bajar por el dashboard con el cursor encima del mapa
          // congela la página y acerca el mapa sin que nadie lo pidiera. El zoom
          // vive en los tres botones de la tarjeta.
          roam: 'move',
          scaleLimit: this.stateMapScaleLimit,
          // Encuadre vigente. Viajan en la opción a propósito: son la única
          // forma de que un setOption notMerge (refresco de colonias) no
          // devuelva el mapa a su vista inicial, y de que restablecer sea exacto.
          center: this.stateMapCenter ?? null,
          zoom: this.stateMapZoom,
          selectedMode: false,
          // OJO: NO fijar aqui ancho ni alto, y menos los cuatro lados juntos.
          // En echarts/lib/util/layout.js, getLayoutRect() resuelve el ancho y el
          // alto ANTES de mirar el aspect:
          //     if (isNaN(width))  { width  = containerWidth  - right - hMargin - left; }
          //     if (isNaN(height)) { height = containerHeight - bottom - vMargin - top; }
          //     if (aspect != null) {
          //       if (isNaN(width) && isNaN(height)) {
          //         aspect > containerWidth / containerHeight
          //           ? width  = containerWidth  * 0.8
          //           : height = containerHeight * 0.8;
          //       }
          //       if (isNaN(width))  { width  = aspect * height; }
          //       if (isNaN(height)) { height = width / aspect; }
          //     }
          // Con los cuatro lados dados, ni width ni height quedan en NaN, el bloque
          // de aspect no ejecuta nada y la geometria se ESTIRA hasta llenar el
          // rectangulo: por eso Mexico salia aplastado a lo ancho (~3.8:1). El
          // aspect que calcula resizeGeo() en coord/geo/geoCreator.js
          // (rect.width / rect.height * aspectScale) se descartaba en silencio.
          //
          // Lo que se hace ahora es lo contrario: NO se da ni ancho ni alto. Las
          // dos unicas claves de layout son 'center' y 'middle', que parsePercent()
          // devuelve como NaN y por lo tanto no despejan ninguna dimension (solo
          // centran despues, en el switch final de getLayoutRect). Asi los dos
          // llegan en NaN al bloque de aspect y ECharts escoge SOLA la dimension
          // que manda, toma el 80% de ella y deriva la otra con el aspect. Dos
          // consecuencias, que son justo lo que se necesita: la proporcion 1.601 se
          // conserva a cualquier ancho, y el mapa NO PUEDE desbordar la tarjeta,
          // porque su lado mayor siempre mide el 80% del lado que lo limita.
          //
          // La otra familia posible era encajar por altura (dar top y bottom y no
          // dar ancho): aprovecha toda la caja, pero entonces el ancho se deriva
          // como aspect * alto y se DESBORDA en cuanto la tarjeta es mas angosta
          // que eso. Medida contra la escalera de alturas de este componente
          // (_map en el SCSS), desbordaba en 19 de 49 anchos del barrido de 320 a
          // 1920px, incluido 1200px exacto, donde pedia 1025px de ancho dentro de
          // una tarjeta de 910px. Por eso se descarto.
          //
          // El 20% de holgura que deja el 80% NO es desperdicio: se reparte a la
          // mitad arriba y abajo (top 'middle'), y esa banda inferior es la que
          // aloja la leyenda del visualMap, que antes se reservaba a mano con 52px
          // fijos sumados al alto en el SCSS.
          left: 'center',
          top: 'middle',
          // El default de ECharts para fuentes geoJSON es 0.75 (ver GEO_DEFAULT_PARAMS
          // en coord/geo/Geo.js), que comprime el eje horizontal y no corresponde a
          // ninguna proyeccion real. El bbox del GeoJSON abarca de 14.53 a 32.72 de
          // latitud, con latitud media 23.63; en una equirectangular un grado de
          // longitud mide cos(23.63) = 0.9162 de un grado de latitud, asi que 0.92 es
          // el factor honesto para Mexico. Resultado: aspect = (31.6423 / 18.1835) *
          // 0.92 = 1.7402 * 0.92 = 1.601, dentro de la proporcion real del pais.
          aspectScale: 0.92,
          label: { show: false },
          itemStyle: {
            // Color por defecto de los estados sin colonias: gris neutro.
            areaColor: this.stateMapEmptyColor,
            borderColor: '#FFFFFF',
            borderWidth: 0.8
          },
          emphasis: {
            // areaColor en null es la forma documentada por ECharts (MapDraw
            // solo aplica el relleno cuando areaColor != null) de NO tocar el
            // color al pasar el mouse. Sin esto, el default del tema pinta el
            // estado de dorado y el usuario veria un color que no corresponde a
            // ningún punto de la escala. El feedback de hover queda en el borde.
            itemStyle: {
              areaColor: null,
              borderColor: '#01579B',
              borderWidth: 1.5
            },
            label: {
              show: true,
              color: '#0F172A',
              fontSize: 11,
              fontWeight: 600
            }
          },
          data: mapData
        }
      ] as any
    };

    this.isStateMapLoading = false;
  }

  /**
   * Copia a la clase el zoom y el centro que el mapa tiene AHORA en pantalla.
   *
   * El roam de ECharts no avisa por ningún callback del resultado: escribe el
   * encuadre en la opción de la serie con MapSeries.setZoom()/setCenter()
   * (echarts/lib/chart/map/MapSeries.js). Y en model/Global.js el arreglo
   * `option.series` guarda LA MISMA referencia de objeto que `seriesModel.option`
   * (optionsByMainType.push(componentModel.option)), así que getOption() —API
   * pública— ya trae el encuadre real, venga de un arrastre o de los botones.
   */
  private captureStateMapView() {

    const seriesOption = this.readStateMapSeriesOption();

    if (!seriesOption) return;

    if (typeof seriesOption.zoom === 'number') this.stateMapZoom = seriesOption.zoom;

    this.stateMapCenter = Array.isArray(seriesOption.center)
      ? [...seriesOption.center]
      : undefined;
  }

  /** Opción viva de la serie del mapa, o undefined si aún no hay instancia. */
  private readStateMapSeriesOption(): any | undefined {

    const chart = this.stateMapChart;

    if (!chart || chart.isDisposed()) return undefined;

    const currentOption = chart.getOption() as any;

    return (currentOption?.series ?? [])[0];
  }

  /**
   * Punto en píxeles alrededor del cual acercar o alejar: el centro del viewport
   * del mapa.
   *
   * Pasarlo es OBLIGATORIO. updateCenterAndZoom() calcula
   *     fixX = (payload.originX - view.x) * (zoom - 1);
   * y sin originX eso da NaN y el mapa desaparece de la tarjeta.
   *
   * Y tiene que ser ESE punto y no otro: la acción recalcula el centro como el
   * dato que quedó en el centro del viewport, así que escalar alrededor de
   * cualquier otro punto haría que el mapa se desplazara solo en cada clic.
   *
   * Cómo se obtiene sin tocar internals: en View.prototype._updateCenterAndZoom()
   * (echarts/lib/coord/View.js) el origen de la transformación de roam ES el
   * centro, de modo que dataToPoint(getCenter()) siempre cae en el centro del
   * viewport. Basta entonces convertir el centro vigente a píxeles con la API
   * pública convertToPixel(), que para una serie de mapa resuelve el sistema de
   * coordenadas por `seriesModel.coordinateSystem` (ver getCoordSys() al final de
   * echarts/lib/coord/geo/Geo.js, comentado "For map series").
   */
  private getStateMapZoomOrigin(
    chart: ECharts
  ): number[] | undefined {

    let center = this.readStateMapSeriesOption()?.center;

    if (!Array.isArray(center)) {

      // Mientras nadie mueva el mapa, `center` sigue en null (es el default de
      // MapSeries) y no hay coordenada que convertir. Un geoRoam de arrastre
      // CERO no cambia un píxel en pantalla, pero obliga a updateCenterAndZoom()
      // a materializar el centro actual en la opción de la serie, que es justo
      // la pieza que falta.
      chart.dispatchAction({
        type: 'geoRoam',
        componentType: 'series',
        seriesIndex: 0,
        dx: 0,
        dy: 0
      });

      center = this.readStateMapSeriesOption()?.center;
    }

    if (!Array.isArray(center)) return undefined;

    const point = chart.convertToPixel({ seriesIndex: 0 }, center as number[]);

    return Array.isArray(point) && point.length === 2 ? point : undefined;
  }

  /** Agrupa las colonias por estado del GeoJSON y ordena el resultado. */
  private setStateSummaries() {

    const summaryByRegion = new Map<string, IStateNeighborhoodSummary>();

    for (const region of this.stateRegionNames) {
      summaryByRegion.set(region.original, {
        name: region.original,
        total: 0,
        active: 0,
        activePercentage: 0
      });
    }

    for (const neighborhood of this.loadedNeighborhoods) {

      const regionName = this.matchRegionName(neighborhood.state);

      if (!regionName) continue;

      const summary = summaryByRegion.get(regionName);

      if (!summary) continue;

      summary.total += 1;

      if (neighborhood.status === true) summary.active += 1;
    }

    // La tabla accesible solo lista los estados con presencia: 32 filas en cero
    // serían ruido para quien navega con lector de pantalla.
    this.stateSummaries = Array.from(summaryByRegion.values())
      .filter(summary => summary.total > 0)
      .map(summary => ({
        ...summary,
        activePercentage: this.getActivePercentage(summary.active, summary.total)
      }))
      .sort(
        (first, second) =>
          second.active - first.active
          || second.total - first.total
          || first.name.localeCompare(second.name, 'es')
      );
  }

  /**
   * Porcentaje de colonias activas de un conjunto, acotado a [0, 100].
   *
   * Los dos extremos NO salen de un redondeo, se deciden comparando enteros.
   * Con Math.round a secas, 199 activas de 200 da 99.5 y se mostraria como
   * "100%": el estado se veria perfecto teniendo una colonia apagada, que es
   * justo el error que el mapa debe delatar. Al otro lado, 1 de 500 da 0.2 y se
   * mostraria como "0%", como si no hubiera nada encendido. Por eso el 100%
   * queda reservado a active === total, el 0% a active === 0, y todo lo demas
   * se aprieta al rango [1, 99] aunque redondee a un extremo.
   */
  private getActivePercentage(
    active: number,
    total: number
  ): number {

    if (total <= 0 || active <= 0) return 0;

    if (active >= total) return 100;

    const percentage = Math.round((active / total) * 100);

    return Math.min(99, Math.max(1, percentage));
  }

  /**
   * Empareja el campo `state` de una colonia con un estado del GeoJSON.
   *
   * Los datos capturados traen el nombre OFICIAL LARGO que devuelve el catálogo
   * de códigos postales, mientras que el GeoJSON usa el nombre corto:
   *   "Coahuila de Zaragoza"            -> "Coahuila"
   *   "Michoacán de Ocampo"             -> "Michoacán"
   *   "Veracruz de Ignacio de la Llave" -> "Veracruz"
   * Con comparación exacta de cadenas esos tres estados se quedarian grises sin
   * ningún aviso, de ahi el segundo intento por prefijo.
   */
  private matchRegionName(
    stateName: string
  ): string | undefined {

    const normalized = this.normalizeStateName(stateName);

    if (!normalized) return undefined;

    const exactMatch = this.stateRegionNames.find(
      region => region.normalized === normalized
    );

    if (exactMatch) return exactMatch.original;

    // stateRegionNames viene ordenado de MÁS LARGO A MÁS CORTO y aquí se
    // devuelve el PRIMERO que coincide, es decir el nombre más específico.
    //
    // Ese orden es obligatorio: "Baja California Sur" empieza con "Baja
    // California". Evaluando en orden arbitrario, TODAS las colonias de BCS se
    // sumarian a Baja California y el mapa mentiría sin que nadie lo note,
    // porque ambos estados existen y ninguno se quedaria gris. No cambiar este
    // sort por uno alfabético.
    const prefixMatch = this.stateRegionNames.find(
      region => normalized.startsWith(region.normalized)
    );

    return prefixMatch?.original;
  }

  /** Minúsculas sin acentos, sin espacios repetidos y sin espacios al borde. */
  private normalizeStateName(
    value: string
  ): string {

    return (value ?? '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .replace(/\s+/g, ' ')
      .trim();
  }

  /** Tooltip del mapa con el conteo exacto, no solo el color. */
  private buildStateTooltip(
    params: any
  ): string {

    const regionName = params?.name ?? '';

    let tooltipHtml = `<div style="font-weight:600;color:#0F172A;margin-bottom:6px;">${regionName}</div>`;

    const summary = params?.data as IStateNeighborhoodSummary | undefined;

    if (!summary || !summary.total) {
      tooltipHtml += `<div style="color:#475569;">Sin colonias registradas</div>`;
      return tooltipHtml;
    }

    const activeLabel = summary.active === 1 ? 'activa' : 'activas';
    const totalLabel = summary.total === 1 ? 'colonia' : 'colonias';

    // El conteo crudo se queda como dato principal: el porcentaje lo acompaña
    // para explicar el color, no para reemplazar el número.
    tooltipHtml += `
      <div style="display:flex;align-items:center;gap:16px;min-width:200px;color:#475569;">
        <span>Colonias ${activeLabel}</span>
        <span style="margin-left:auto;font-weight:600;color:#0F172A;white-space:nowrap;">${this.numberFormatter.format(summary.active)} de ${this.numberFormatter.format(summary.total)}</span>
      </div>
      <div style="display:flex;align-items:center;gap:16px;color:#475569;margin-top:2px;">
        <span>Proporción activa</span>
        <span style="margin-left:auto;font-weight:600;color:#0F172A;white-space:nowrap;">${summary.activePercentage}%</span>
      </div>
      <div style="color:#475569;margin-top:2px;">${this.numberFormatter.format(summary.total)} ${totalLabel} en total</div>
    `;

    return tooltipHtml;
  }

  private setStateMapAriaLabel() {

    const totalNeighborhoods = this.stateSummaries.reduce(
      (sum, summary) => sum + summary.total,
      0
    );

    const totalActive = this.stateSummaries.reduce(
      (sum, summary) => sum + summary.active,
      0
    );

    if (!totalNeighborhoods) {
      this.stateMapAriaLabel = 'Mapa de México por estado. No hay colonias registradas.';
      return;
    }

    const globalPercentage = this.getActivePercentage(totalActive, totalNeighborhoods);

    // El estado que se anuncia es el de MAYOR PORCENTAJE, porque eso es lo que
    // el color muestra. No sirve stateSummaries[0]: esa lista va ordenada por
    // conteo para la tabla. En empate gana el de más colonias, porque 100% de
    // 40 colonias dice bastante más que 100% de 1.
    const leader = this.stateSummaries.reduce(
      (best, current) =>
        current.activePercentage > best.activePercentage
        || (current.activePercentage === best.activePercentage && current.total > best.total)
          ? current
          : best
    );

    this.stateMapAriaLabel = `Mapa de México coloreado por el porcentaje de colonias activas sobre el total de cada estado, `
      + `de 0 por ciento en rojo a 100 por ciento en verde. Los estados sin colonias quedan en gris. `
      + `${this.stateSummaries.length} estados con presencia, `
      + `${totalNeighborhoods} colonias en total, ${totalActive} activas, ${globalPercentage} por ciento. `
      + `El estado con mayor porcentaje de colonias activas es ${leader.name} con ${leader.activePercentage} por ciento, `
      + `${leader.active} de ${leader.total}.`;
  }

}
